import React, { useState } from "react";
import styles from "./stepSix.module.css";
import Header from "../../Header/Header";
import { useLocation, useNavigate } from "react-router-dom";
import Slider from "@mui/material/Slider";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../../config"; // Make sure this points to your config
import ProgressBar from "../../../components/ProgressBar";
import { postApi } from "../../AppApis";
import { showErrorToast } from "../../utils";

function StepSix() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    console.log("Location State:", location.state);
    const [budgetForHome, setBudgetForHome] = useState(1);
    const [monthlyMortgage, setMonthlyMortgage] = useState(1); // Default value as shown in Postman
    const [downPayment, setDownPayment] = useState(1); // Default value as shown in Postman
    const [firstQuestionAnswer, setFirstQuestionAnswer] = useState(null); // For "Are you interested in a VA loan?"
    const [secondQuestionAnswer, setSecondQuestionAnswer] = useState(null); // For "Are you buying this home on your own?"
    const [thirdQuestionAnswer, setThirdQuestionAnswer] = useState(null); // For "Do you have a real estate agent?"
    const [activeSlider, setActiveSlider] = useState(null);

    // Handle the slider changes
    const handleSliderChange = (setter) => (event, newValue) => {
        setter(newValue);
    };

    const handleMouseEnter = (sliderName) => {
        setActiveSlider(sliderName);
    };

    const handleMouseLeave = () => {
        setActiveSlider(null);
    };

    // API integration
    const handleGetQualifiedClick = async (e) => {
        e.preventDefault();

        if (!firstQuestionAnswer || !secondQuestionAnswer || !thirdQuestionAnswer) {
            showErrorToast("Please answer all questions.");
            return;
        }
        try {
            setIsLoading(true);
            console.log({
                ...location.state,
                budget: Number(budgetForHome) * 1000,
                monthly_payment: Number(monthlyMortgage) * 1000,
                down_payment: Number(downPayment) * 1000,
            });
            const payload = {
                timeline_stage: {
                    stage: location.state.timeline,
                    zipcode: location.state.address_data.zipCode,
                },
                home_use_type: location.state.homebuying,
                co_borrower: secondQuestionAnswer,
                us_citizen: location.state.are_you_us_citizen,
                current_debts: location.state.current_debts,
                monthly_expenses: location.state.monthly_expenses,
                bankruptcy_history: location.state.bankruptcy_history,
                annual_income: location.state.annual_income,
                real_estate_agent: thirdQuestionAnswer,
                citizenship_financial_history: location.state.foreclosure_history,
                va_first_time_homebuyer: location.state.is_first_time_buyer,
                credit_score: location.state.credit_score,
                budget_payment: {
                    budget: Number(budgetForHome) * 1000 + "$",
                    monthly_payment: Number(monthlyMortgage) * 1000 + "$",
                    down_payment: Number(downPayment) * 1000 + "$",
                },
                address: location.state.address_data.address,
                project_type: "onmls",
            };

            const data = {
                data: payload,
                service_name: "Financing",
            };
            const response = await postApi("/property-services/request_service", data);
            if (response?.response && response.response?.detail) {
                showErrorToast(`Failed to submit: ${response.response.detail || "Please try again."}`);
            }
            if (response?.error === 401) {
                navigate("/login");
            } else if (response.response) {
                navigate("/get-qualified/success");
            } else {
                console.log("Error Response:", response?.detail);
                showErrorToast(`Failed to submit: ${response?.detail?.message || "Please try again."}`);
            }
        } catch (error) {
            console.error("Error submitting the form:", error);
            showErrorToast("An error occurred. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleOptionClick = (setter, option) => {
        setter(option);
    };

    return (
        <main className={`${styles.preQualificationForm} tw-h-screen tw-overflow-auto tw-bg-background_color`}>
            <Header title="Apply For Financing" />
            <section className={`${styles.formContent} tw-items-center`}>
                <div className="tw-w-full tw-mt-[40px]">
                    <ProgressBar currentStep={6} totalSteps={6} />
                </div>
                <form className={`${styles.questionnaireForm} tw-w-full max-lg:tw-w-[80%] tw-mt-10`} onSubmit={handleGetQualifiedClick}>
                    <div className="tw-flex tw-gap-2 tw-flex-wrap">
                        <div>
                            <h2 className={`${styles.formTitle} tw-mt-3`}>Budget Details</h2>
                            <p>
                                To help us move forward smoothly with your mortgage application, please provide the following income and employment
                                details
                            </p>
                        </div>
                        <div className="tw-flex tw-flex-wrap  tw-gap-4 tw-w-full">
                            <div className={styles.questionContainer}>
                                <h2 className={styles.questionTitle}>What is your budget for purchasing a new home?</h2>
                                <Slider
                                    value={budgetForHome}
                                    onChange={handleSliderChange(setBudgetForHome)}
                                    onMouseEnter={() => handleMouseEnter("budgetForHome")}
                                    onMouseLeave={handleMouseLeave}
                                    min={100}
                                    max={1000} // Adjusted maximum for larger budgets
                                    marks={[
                                        { value: 100, label: "100k" },
                                        { value: 1000, label: "1m" },
                                    ]}
                                    className="tw-text-primary"
                                    valueLabelFormat={(value) => (value === 1000 ? "1m" : `${value}k`)}
                                    valueLabelDisplay={activeSlider === "budgetForHome" ? "on" : "auto"}
                                    sx={{ width: "90%", marginLeft: "10px" }}
                                />
                            </div>

                            <div className={styles.questionContainer}>
                                <h2 className={styles.questionTitle}>What monthly mortgage payment are you comfortable with?</h2>
                                <Slider
                                    value={monthlyMortgage}
                                    onChange={handleSliderChange(setMonthlyMortgage)}
                                    onMouseEnter={() => handleMouseEnter("monthlyMortgage")}
                                    onMouseLeave={handleMouseLeave}
                                    min={10}
                                    max={250} // Ensure a realistic maximum
                                    marks={[
                                        { value: 10, label: "10k" },
                                        { value: 250, label: "250k" },
                                    ]}
                                    valueLabelFormat={(value) => `${value}k`}
                                    valueLabelDisplay={activeSlider === "monthlyMortgage" ? "on" : "auto"}
                                    className="tw-text-primary"
                                />
                            </div>

                            <div className={styles.questionContainer}>
                                <h2 className={styles.questionTitle}>
                                    What amount are you prepared to spend initially for your down payment and closing costs?
                                </h2>
                                <Slider
                                    value={downPayment}
                                    onChange={handleSliderChange(setDownPayment)}
                                    onMouseEnter={() => handleMouseEnter("downPayment")}
                                    onMouseLeave={handleMouseLeave}
                                    min={5}
                                    max={25} // Ensure a realistic maximum
                                    marks={[
                                        { value: 5, label: "5k" },
                                        { value: 25, label: "25k" },
                                    ]}
                                    valueLabelFormat={(value) => `${value}k`}
                                    valueLabelDisplay={activeSlider === "downPayment" ? "on" : "auto"}
                                    className="tw-text-primary"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="tw-flex tw-flex-wrap tw-gap-4 tw-w-full tw-mt-4">
                        {/* First Question - VA Loan */}
                        <div>
                            <label className={styles.questionTitle}>Are you interested in a VA Loan for veterans?</label>
                            <div className={`${styles.optionsContainer} ${styles.rowLayout}`}>
                                {["Yes", "No"].map((option, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.option} ${firstQuestionAnswer === option ? "tw-bg-primary hover:tw-bg-primary_hover tw-text-white" : ""}`}
                                        onClick={() => handleOptionClick(setFirstQuestionAnswer, option)}
                                    >
                                        {option}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Second Question - Credit Score (By Myself or Co-borrower) */}
                        <div>
                            <label className={styles.questionTitle}>Are you borrowing with a Co-Borrower?</label>
                            <div className={`${styles.optionsContainer} ${styles.rowLayout}`}>
                                {["Yes", "No"].map((option, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.option} ${secondQuestionAnswer === option ? "tw-bg-primary hover:tw-bg-primary_hover tw-text-white" : ""}`}
                                        onClick={() => handleOptionClick(setSecondQuestionAnswer, option)}
                                    >
                                        {option}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Third Question - Real Estate Agent */}
                        <div>
                            <label className={styles.questionTitle}>Do you have a real estate agent?</label>
                            <div className={`${styles.optionsContainer} ${styles.rowLayout}`}>
                                {["Yes", "No"].map((option, index) => (
                                    <div
                                        key={index}
                                        className={`${styles.option} ${thirdQuestionAnswer === option ? "tw-bg-primary hover:tw-bg-primary_hover tw-text-white" : ""}`}
                                        onClick={() => handleOptionClick(setThirdQuestionAnswer, option)}
                                    >
                                        {option}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button type="submit" disabled={isLoading} className={`${styles.nextButton} tw-bg-primary hover:tw-bg-primary_hover`}>
                        {isLoading ? "Loading..." : "Next"}
                    </button>
                </form>
            </section>
        </main>
    );
}

export default StepSix;
